import React from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';

/**
 * Alert Component
 * 
 * Production-grade alert/notification banner with comprehensive features:
 * - Variants (info, success, warning, error)
 * - Title + description
 * - Icon (auto or custom)
 * - Action button
 * - Dismissible (with X button)
 * - Color-blind friendly (icons + colors)
 * - Dark mode compatible
 * - Mobile-first responsive
 * 
 * @example
 * ```tsx
 * <Alert
 *   variant="warning"
 *   title="Storage almost full"
 *   description="You're running out of storage space. Consider upgrading your plan."
 *   action={{
 *     label: "Upgrade",
 *     onClick: () => navigateToUpgrade()
 *   }}
 *   dismissible
 *   onDismiss={() => setShowAlert(false)}
 * />
 * ```
 */

export interface AlertAction {
  label: string;
  onClick: () => void;
}

export interface AlertProps {
  /** Alert variant */
  variant: 'info' | 'success' | 'warning' | 'error';
  /** Alert title */
  title?: string;
  /** Alert description */
  description: string;
  /** Custom icon (or false to hide auto icon) */
  icon?: React.ReactNode | false;
  /** Action button */
  action?: AlertAction;
  /** Can be dismissed */
  dismissible?: boolean;
  /** Dismiss callback */
  onDismiss?: () => void;
  /** Additional CSS classes */
  className?: string;
}

export const Alert: React.FC<AlertProps> = ({
  variant,
  title,
  description,
  icon,
  action,
  dismissible = false,
  onDismiss,
  className = '',
}) => {
  // Get auto icon based on variant
  const getAutoIcon = () => {
    switch (variant) {
      case 'success':
        return <CheckCircle className="h-5 w-5 flex-shrink-0" />;
      case 'error':
        return <AlertCircle className="h-5 w-5 flex-shrink-0" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 flex-shrink-0" />;
      case 'info':
      default:
        return <Info className="h-5 w-5 flex-shrink-0" />;
    }
  };

  // Determine which icon to show
  const displayIcon = icon === false ? null : icon || getAutoIcon();

  // Variant-specific colors
  const variantClasses = {
    info: {
      container: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700',
      icon: 'text-blue-500 dark:text-blue-400',
      title: 'text-blue-900 dark:text-blue-100',
      description: 'text-blue-700 dark:text-blue-300',
      action: 'text-blue-700 dark:text-blue-300 hover:text-blue-900 dark:hover:text-blue-100 bg-blue-100 dark:bg-blue-800 hover:bg-blue-200 dark:hover:bg-blue-700',
      dismiss: 'text-blue-500 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-800',
    },
    success: {
      container: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700',
      icon: 'text-green-500 dark:text-green-400',
      title: 'text-green-900 dark:text-green-100',
      description: 'text-green-700 dark:text-green-300',
      action: 'text-green-700 dark:text-green-300 hover:text-green-900 dark:hover:text-green-100 bg-green-100 dark:bg-green-800 hover:bg-green-200 dark:hover:bg-green-700',
      dismiss: 'text-green-500 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-800',
    },
    warning: {
      container: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-700',
      icon: 'text-yellow-600 dark:text-yellow-400',
      title: 'text-yellow-900 dark:text-yellow-100',
      description: 'text-yellow-700 dark:text-yellow-300',
      action: 'text-yellow-700 dark:text-yellow-300 hover:text-yellow-900 dark:hover:text-yellow-100 bg-yellow-100 dark:bg-yellow-800 hover:bg-yellow-200 dark:hover:bg-yellow-700',
      dismiss: 'text-yellow-600 dark:text-yellow-400 hover:bg-yellow-100 dark:hover:bg-yellow-800',
    },
    error: {
      container: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700',
      icon: 'text-red-500 dark:text-red-400',
      title: 'text-red-900 dark:text-red-100',
      description: 'text-red-700 dark:text-red-300',
      action: 'text-red-700 dark:text-red-300 hover:text-red-900 dark:hover:text-red-100 bg-red-100 dark:bg-red-800 hover:bg-red-200 dark:hover:bg-red-700',
      dismiss: 'text-red-500 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-800',
    },
  };

  const colors = variantClasses[variant];

  return (
    <div
      className={`
        ${colors.container}
        border
        rounded-lg
        p-4
        ${className}
      `.trim().replace(/\s+/g, ' ')}
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        {displayIcon && (
          <div className={colors.icon}>
            {displayIcon}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          {title && (
            <h3 className={`text-sm font-semibold mb-1 ${colors.title}`}>
              {title}
            </h3>
          )}
          <p className={`text-sm ${colors.description}`}>
            {description}
          </p>

          {/* Action Button */}
          {action && (
            <button
              type="button"
              onClick={action.onClick}
              className={`
                mt-3
                px-3
                py-1.5
                text-sm
                font-medium
                rounded-md
                transition-colors
                ${colors.action}
              `.trim().replace(/\s+/g, ' ')}
            >
              {action.label}
            </button>
          )}
        </div>

        {/* Dismiss Button */}
        {dismissible && onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className={`
              flex-shrink-0
              rounded-md
              p-1.5
              inline-flex
              transition-colors
              focus:outline-none
              focus:ring-2
              focus:ring-offset-2
              focus:ring-blue-500
              dark:focus:ring-blue-400
              ${colors.dismiss}
            `.trim().replace(/\s+/g, ' ')}
            aria-label="Dismiss alert"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  );
};

Alert.displayName = 'Alert';

